import { useEventTimezoneQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

/**
 * Formats a date as ISO 8601 with timezone
 * Converts a date with a specific local time to a timezoned date with same local time
 * and returns the ISO 8601 string with timezone
 * @param date - The date to format
 * @param timezone - The timezone to use
 * @returns The formatted date
 */
export const formatDateAsISO8601TZ = (date: Date | null, timezone?: string) => {
  if (!date) {
    return null;
  }
  // Convert to UTC using the specified timezone
  const newDate = zonedTimeToUtc(date, timezone ?? DEFAULT_TIMEZONE);

  return timezone ? `${newDate.toISOString()}[${timezone}]` : newDate.toISOString();
};

/**
 * Parses an ISO 8601 date string with optional timezone annotation.
 * Converts UTC time to the specified timezone's local time using utcToZonedTime,
 * which shifts the Date object's internal UTC value to represent the local time.
 * This allows Intl.DateTimeFormat to display the correct time consistently across all browser timezones.
 *
 * @param isoString - ISO 8601 string with optional timezone, e.g., "2025-10-06T17:00:00.000Z[America/Phoenix]"
 * @returns Object containing the shifted datetime Date object and the IANA timezone identifier, or null if input is falsy
 *
 * @example
 * parseISO8601WithTimezone("2025-10-06T17:00:00.000Z[America/Phoenix]")
 * // Returns: { datetime: Date(2025-10-06T10:00:00.000Z), timezone: "America/Phoenix" }
 * // The datetime is shifted so 10:00 UTC represents 10:00 AM Phoenix time
 *
 * @example
 * parseISO8601WithTimezone(null)
 * // Returns: null
 */
export const parseISO8601WithTimezone = (isoString: string | null | undefined): { datetime: Date; timezone: string | undefined } | null => {
  // PR #11978 Comment #1: Handle null/undefined input
  if (!isoString) return null;

  const [datetimeRaw, zonePart] = isoString.split('[');

  if (!zonePart) {
    return { datetime: new Date(datetimeRaw), timezone: undefined };
  }

  const timezone = zonePart.replace(']', '');
  const datetime = utcToZonedTime(new Date(datetimeRaw), timezone);

  return { datetime, timezone };
};

export const useGetEventTimezone = (): string | undefined => {
  const { data } = useEventTimezoneQuery({
    variables: { eventId: useEventInfo().eventInfo?.eventId ?? '' },
    batchMode: 'fast',
  });

  return data?.eventById?.info.timezone ?? undefined;
};
